import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import {
  AuditService,
  OrderableIngredient,
  OrderableItem,
  OrderedItem,
  OrderedItemService,
  SizePrice,
  GenericObject,
  StoreConfig,
  OrderedIngredient,
} from 'ngx-web-api';
import { Subscription, Observable, combineLatest } from 'rxjs';
import { ItemEditorService } from '../../core/services/item-editor.service';
import { ViewportService } from '../../core/services/viewport.service';
import { ViewPort } from '../../domain/view-port.model';
import { Animations } from 'app/animations';
import { HighlightElementsService } from 'app/shared/highlight-elements.service';
import { EditingItemChoicesService } from 'app/core/services/editing-item-choices.service';
import { FtsCurrencyPipe } from 'app/shared/pipes/fts-currency.pipe';
import { faSort } from '@fortawesome/free-solid-svg-icons';
import { HalfsOrderingService } from 'app/core/services/halfs-ordering.service';

@Component({
  selector: 'fts-item-editor-controls',
  templateUrl: './item-editor-controls.component.html',
  styleUrls: ['./item-editor-controls.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [Animations.expanded],
})
export class ItemEditorControlsComponent implements OnChanges, OnDestroy {
  @Input()
  orderableItem: OrderableItem;
  @Input()
  item: OrderedItem;
  @Input()
  price: number;
  @Input()
  ingredientChoicesAsSteps: boolean;
  @Input()
  updatingOrderSubscription: Subscription;
  @Input()
  storeConfig: StoreConfig;
  @Input()
  inItemEditorModal = false;
  @Input()
  isHalfAndHalf = false;

  @Output()
  quantityChange: EventEmitter<number> = new EventEmitter();
  @Output()
  orderUpdate: EventEmitter<any> = new EventEmitter();
  @Output()
  sizeChanged: EventEmitter<any> = new EventEmitter<string>();
  @Output()
  previousQuantity: EventEmitter<number> = new EventEmitter();

  calories: string;
  orderableIngredients: OrderableIngredient[];
  allIngredients: OrderedIngredient[];
  priceString: string;
  hasSingleSize = false;
  hasOnlyOneSizeAvailable = false;
  isItemValid: boolean;
  errors: GenericObject<string[]> = {};

  private subscriptions: Subscription[] = [];
  public viewPort$: Observable<ViewPort>;
  faSort = faSort;

  disableOrderButton = false;

  constructor(
    private auditService: AuditService,
    private orderedItemService: OrderedItemService,
    public itemEditorService: ItemEditorService,
    private viewPortService: ViewportService,
    private currencyPipe: FtsCurrencyPipe,
    private highlightService: HighlightElementsService,
    private editingService: EditingItemChoicesService,
    private halfsOrderingService: HalfsOrderingService
  ) {
    this.viewPort$ = this.viewPortService.viewport$;
    combineLatest([this.halfsOrderingService.leftHalfOrderedItem$, this.halfsOrderingService.rightHalfOrderedItem$]).subscribe(
      ([leftOrderedItem, rightOrderedItem]) => {
        this.disableOrderButton = !leftOrderedItem || !rightOrderedItem;
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderableItem']) {
      const currentOrderableItem = changes.orderableItem.currentValue;
      this.orderableIngredients = this.orderableItem.choices.map(choice => choice.ingredients || []).reduce((a, b) => a.concat(b), []);
      this.hasSingleSize = currentOrderableItem.sizePrices[0].size === 'Size';
      this.hasOnlyOneSizeAvailable = currentOrderableItem.sizePrices.length === 1 && currentOrderableItem.sizePrices[0].size !== 'Size';
    }
    if (changes['item']) {
      this.allIngredients = this.orderedItemService.getAllIngredients(this.item);
    }
    if (changes['price']) {
      this.priceString = this.currencyPipe.transform(this.price, 'USD', 'symbol');
    }

    if (!!this.orderableItem && !!this.item) {
      this.calcIsItemValid();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onQuantityChanged(quantity: number) {
    this.quantityChange.emit(quantity);
  }

  onSizeChanged(size: string) {
    this.auditService.createAudit(() => `Size changed to ${size} for item "${this.orderableItem.name}"`);
    this.item = Object.assign(this.item, { specialtyLeftHalf: undefined, specialtyRightHalf: undefined });
    this.sizeChanged.emit(size);
    this.itemEditorService.setSizeChanged(size);
  }

  onOrderUpdate() {
    if (!this.item.isValid()) {
      this.editingService.selectFirstInvalidChoice(this.orderableItem, this.item);
      this.highlightService.highLightAll();
      return;
    }
    this.orderUpdate.emit();
  }

  calcIsItemValid() {
    const ordered = this.item.copy();
    const orderable = this.orderableItem.copy();
    this.isItemValid = this.orderedItemService.isValid(ordered, orderable);
  }

  trackBySize(_index: number, sp: SizePrice) {
    return sp.size;
  }

  onQuantityError(errors: GenericObject<string[]>) {
    this.errors = errors;
  }
}
